import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GERI_ALMA_DAKIKA, MESAJ_EN_COK } from "@koclukpanel/contracts";
import {
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  NotKutusu,
  Rozet,
  Secim,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { MesaiAyari } from "./MesaiAyari";
import { MesajSablonlari } from "./MesajSablonlari";
import {
  ApiHatasi,
  benGetir,
  mesajEkBaglantisi,
  mesajEkliGonder,
  mesajGeriAl,
  mesajGonder,
  mesajKisileri,
  mesajKutum,
  mesajSablonlarim,
  mesajOkundu,
  mesajYazisma,
  type MesajEki,
  type MesajKanali,
  type YazismaMesaji,
  type YazismaOzeti,
} from "./api";

/**
 * MESAJLAŞMA EKRANI — F-MSG
 *
 * ================================================================
 * SUNUCU YAZILMIŞTI, EKRAN YOKTU
 * ================================================================
 * Uçlar, kurallar ve denetim kaydı 06.09.2026'da bitti; hiçbir arayüz
 * onları çağırmıyordu. Bu depoda daha önce ölçülmüş bir hata sınıfı
 * (`Bildirimler.tsx` notu): çalışan ama kimsenin ulaşamadığı bir
 * modül, olmayan bir modüldür.
 *
 * ================================================================
 * KANAL SEÇİMİ EKRANDA, KURAL SUNUCUDA
 * ================================================================
 * Ekran hangi kanalı kimin açabileceğini KENDİ bilmiyor: alıcı
 * listesini sunucudan alıyor (`/mesaj/kisiler`) ve reddi sunucudan
 * duyuyor. Kuralı buraya kopyalasaydık, `mesaj-kurallari.ts` ile
 * ayrıştığı gün ya yazılabilecek birini gizler ya da yazılamayacak
 * birini gösterirdi.
 *
 * ================================================================
 * "OKUNDU" DAMGASININ BEDELİ VAR
 * ================================================================
 * Yazışmayı açmak karşı tarafın geri alma hakkını bitiriyor
 * (F-MSG-01b). Bu yüzden damga yazışma AÇILDIĞINDA atılıyor, gelen
 * kutusu çizilirken değil — kutuyu görmek okumak değildir.
 */

const KANAL_ADI: Record<MesajKanali, string> = {
  KOC: "Koçluk",
  KURUM: "Kurum",
  PERSONEL: "Personel",
};

const KANAL_TONU: Record<MesajKanali, "bilgi" | "notr" | "uyari"> = {
  KOC: "bilgi",
  KURUM: "uyari",
  PERSONEL: "notr",
};

/** Okunabilir boyut — "1,4 MB". Ekin ne kadar veri olduğu görünür olsun. */
export function ekBoyutu(bayt: number): string {
  if (bayt >= 1024 * 1024) return `${(bayt / (1024 * 1024)).toFixed(1)} MB`;
  if (bayt >= 1024) return `${Math.round(bayt / 1024)} KB`;
  return `${bayt} B`;
}

/**
 * Mesaj eki — F-MSG-03.
 *
 * ================================================================
 * BAĞLANTI ÖNCEDEN ÜRETİLMİYOR, TIKLAYINCA ÜRETİLİYOR
 * ================================================================
 * İmzalı bağlantı 15 dakika yaşıyor. Yazışma açılırken bütün ekler
 * için üretseydik, ekranı açık bırakan kullanıcının bağlantıları
 * sessizce ölürdü ve tıklayınca hiçbir şey olmazdı. Ayrıca her okuma
 * denetim kaydına geçiyor — açmadığı eki indirmiş saymak yanlış olurdu.
 */
function EkSatiri({ messageId, ek }: { messageId: string; ek: MesajEki }) {
  const [hata, setHata] = useState<string | null>(null);
  const ac = useMutation({
    mutationFn: () => mesajEkBaglantisi(messageId),
    onSuccess: (s) => {
      setHata(null);
      window.open(s.url, "_blank", "noopener");
    },
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Ek açılamadı."),
  });

  return (
    <div className="mt-1">
      <Buton
        gorunum="ikincil"
        onClick={() => ac.mutate()}
        yukleniyor={ac.isPending}
        yukleniyorMetni="Açılıyor"
      >
        📎 {ek.ad} · {ekBoyutu(ek.boyut)}
      </Buton>
      {hata && (
        <p role="alert" className="text-hata-700 mt-1 text-sm">
          {hata}
        </p>
      )}
    </div>
  );
}

function saat(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Mesaj hâlâ geri alınabilir mi — EKRANIN TAHMİNİ.
 *
 * Son sözü sunucu söylüyor: okunmuş bir mesaj süre dolmamış olsa bile
 * geri alınamıyor ve ekran karşı tarafın okuma damgasını GÖRMÜYOR.
 * Bu yüzden düğmeye basıldığında sunucu "okundu" diyebilir; o cevap
 * kullanıcıya aynen gösteriliyor.
 *
 * Süreye hiç bakmadan düğmeyi hep göstermek de bir seçenekti ama iki
 * gün önceki mesajın altında duran "Geri al" düğmesi, olmayan bir hak
 * varmış gibi görünürdü.
 */
export function geriAlmaGorunurMu(mesaj: YazismaMesaji, simdi: Date): boolean {
  if (!mesaj.benimMi || mesaj.geriAlindiMi) return false;
  const gecen = (simdi.getTime() - new Date(mesaj.createdAt).getTime()) / 60_000;
  return gecen <= GERI_ALMA_DAKIKA;
}

function KutuSatiri({
  yazisma,
  secili,
  onSec,
}: {
  yazisma: YazismaOzeti;
  secili: boolean;
  onSec: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSec}
        aria-current={secili ? "true" : undefined}
        className={`border-notr-200 w-full border-b px-3 py-3 text-left last:border-b-0 ${
          secili ? "bg-marka-50" : "hover:bg-notr-50"
        }`}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`text-notr-900 min-w-0 truncate text-sm ${
              yazisma.okunmamis > 0 ? "font-semibold" : ""
            }`}
          >
            {yazisma.karsiTaraf}
          </span>
          {yazisma.okunmamis > 0 && <Rozet ton="bilgi">{yazisma.okunmamis}</Rozet>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Rozet ton={KANAL_TONU[yazisma.kanal]}>{KANAL_ADI[yazisma.kanal]}</Rozet>
          {yazisma.arsivlendiMi && <Rozet ton="notr">Arşiv</Rozet>}
          <span className="text-notr-600 text-xs tabular-nums">{saat(yazisma.sonMesajAt)}</span>
        </div>
      </button>
    </li>
  );
}

function MesajBalonu({
  mesaj,
  onGeriAl,
  geriAlYukleniyor,
  gorunumde,
}: {
  mesaj: YazismaMesaji;
  onGeriAl: () => void;
  geriAlYukleniyor: boolean;
  gorunumde: boolean;
}) {
  const [simdi, setSimdi] = useState(() => new Date());

  /*
    Geri alma penceresi SAYAÇLA kapanıyor. Sayaç olmasaydı düğme süresi
    geçtikten sonra da ekranda durur, basan kişi hata alırdı — hata
    doğru olurdu ama arayüz yalan söylemiş olurdu.
  */
  useEffect(() => {
    if (!mesaj.benimMi || mesaj.geriAlindiMi) return;
    const t = setInterval(() => setSimdi(new Date()), 5_000);
    return () => clearInterval(t);
  }, [mesaj.benimMi, mesaj.geriAlindiMi]);

  return (
    <li className={`flex flex-col ${mesaj.benimMi ? "items-end" : "items-start"}`}>
      <div
        className={`rounded-alan max-w-[85%] px-3 py-2 ${
          mesaj.benimMi ? "bg-marka-50" : "bg-notr-50"
        }`}
      >
        {!mesaj.benimMi && <p className="text-notr-600 text-xs font-medium">{mesaj.gonderenAd}</p>}
        {mesaj.geriAlindiMi ? (
          /*
            GÖVDE SİLİNMEDİ, GİZLENDİ. Sunucu `null` gönderiyor; kayıt
            veritabanında duruyor ve gerekçeli denetim okumasında
            görünüyor (F-MSG-06). Ekranda "silindi" DEMİYORUZ —
            silinmedi ve kullanıcıya yanlış bir güvence vermek, geri
            almayı kanıt yok etmenin meşru yolu sanmasına yol açardı.
          */
          <p className="text-notr-600 text-sm italic">Bu mesaj geri alındı.</p>
        ) : (
          <p className="text-notr-900 text-sm break-words whitespace-pre-wrap">{mesaj.govde}</p>
        )}
        {mesaj.ek && <EkSatiri messageId={mesaj.id} ek={mesaj.ek} />}
        <p className="text-notr-600 mt-1 text-xs tabular-nums">{saat(mesaj.createdAt)}</p>
      </div>
      {!gorunumde && geriAlmaGorunurMu(mesaj, simdi) && (
        <Buton
          gorunum="sessiz"
          onClick={onGeriAl}
          yukleniyor={geriAlYukleniyor}
          yukleniyorMetni="Geri alınıyor"
        >
          Geri al
        </Buton>
      )}
    </li>
  );
}

function Yazisma({ id, gorunumde }: { id: string; gorunumde: boolean }) {
  const qc = useQueryClient();
  const [taslak, setTaslak] = useState("");
  const [dosya, setDosya] = useState<File | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const liste = useRef<HTMLUListElement | null>(null);
  const dosyaAlani = useRef<HTMLInputElement | null>(null);

  const veri = useQuery({
    queryKey: ["mesaj-yazisma", id],
    queryFn: () => mesajYazisma(id),
    retry: false,
  });

  /*
    Şablon listesi ayarlar kartıyla AYNI sorgu anahtarını paylaşıyor:
    yeni bir yanıt kaydedildiğinde yazma kutusundaki liste
    kendiliğinden tazeleniyor. İki ayrı anahtar olsaydı koç yeni
    yanıtını kaydeder ve kutuda göremezdi.
  */
  const sablonSorgusu = useQuery({
    queryKey: ["mesaj-sablonlar"],
    queryFn: mesajSablonlarim,
    retry: false,
  });
  const sablonlar = sablonSorgusu.data?.sablonlar ?? [];

  /*
    OKUNDU DAMGASI YAZIŞMA AÇILINCA. Gelen kutusu çizilirken atsaydık,
    kutuya bakmak "okudum" sayılır ve karşı tarafın geri alma hakkı
    kullanıcı hiçbir şey okumadan biterdi.
  */
  const damga = useMutation({
    mutationFn: () => mesajOkundu(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mesaj-kutum"] }),
  });
  const damgaAt = damga.mutate;
  const basarili = veri.isSuccess;
  useEffect(() => {
    // Görünüm kipinde "okundu" damgası yazılmaz: sunucu da reddeder (GORUNUM_KISITI) ve
    // yönetici bakmakla uzmanın yazışmasını okundu yapmamalı.
    if (basarili && !gorunumde) damgaAt();
  }, [basarili, gorunumde, id, damgaAt]);

  const mesajSayisi = veri.data?.mesajlar.length;
  /*
    LİSTENİN KENDİSİ KAYDIRILIYOR, `scrollIntoView` DEĞİL. İkincisi
    kapsayan her kaydırılabilir kutuyu birden oynatır ve yazışma
    sayfanın ortasındayken bütün sayfayı zıplatır — üstelik jsdom'da
    hiç yok, yani ekranın testi de o yüzden düşüyordu.
  */
  useEffect(() => {
    const el = liste.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [mesajSayisi]);

  const tazele = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["mesaj-yazisma", id] }),
      qc.invalidateQueries({ queryKey: ["mesaj-kutum"] }),
    ]);
  };

  const kanal = veri.data?.kanal;
  const gonder = useMutation({
    mutationFn: () =>
      dosya
        ? mesajEkliGonder({ kanal: kanal!, conversationId: id, govde: taslak, dosya })
        : mesajGonder({ kanal: kanal!, conversationId: id, govde: taslak }),
    onSuccess: async () => {
      setTaslak("");
      setDosya(null);
      /*
        `input type=file` DEĞERİ REACT DURUMUNDAN AYRI yaşıyor: durumu
        temizlemek alanın kendisini temizlemiyor ve aynı dosya ikinci
        kez seçilemez hâle geliyordu (tarayıcı "değişmedi" diyor).
      */
      if (dosyaAlani.current) dosyaAlani.current.value = "";
      setHata(null);
      await tazele();
    },
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Mesaj gönderilemedi."),
  });

  const geriAl = useMutation({
    mutationFn: (messageId: string) => mesajGeriAl(messageId),
    onSuccess: async () => {
      setHata(null);
      await tazele();
    },
    /*
      SUNUCUNUN SEBEBİ AYNEN GÖSTERİLİYOR. "Okundu" ile "süre geçti"
      ayrı iki cümle ve kullanıcıya farklı şey söylüyorlar: ilki "karşı
      taraf gördü", ikincisi "geç kaldın". Tek bir "geri alınamadı"
      mesajı ikisini de gizlerdi.
    */
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Mesaj geri alınamadı."),
  });

  if (veri.isPending) return <Iskelet satir={5} />;

  if (veri.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Yazışma açılamadı"
        aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const y = veri.data;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-notr-200 flex items-center gap-2 border-b pb-2">
        <Rozet ton={KANAL_TONU[y.kanal]}>{KANAL_ADI[y.kanal]}</Rozet>
        {y.baslik && <span className="text-notr-700 text-sm">{y.baslik}</span>}
      </div>

      <ul ref={liste} className="flex flex-1 flex-col gap-3 overflow-y-auto py-3">
        {y.mesajlar.length === 0 ? (
          <li className="text-notr-600 text-sm">Bu yazışmada henüz mesaj yok.</li>
        ) : (
          y.mesajlar.map((m) => (
            <MesajBalonu
              key={m.id}
              mesaj={m}
              onGeriAl={() => geriAl.mutate(m.id)}
              geriAlYukleniyor={geriAl.isPending && geriAl.variables === m.id}
              gorunumde={gorunumde}
            />
          ))
        )}
      </ul>

      {gorunumde ? (
        /*
          GÖRÜNÜM KİPİNDE YAZMA KUTUSU ÇİZİLMİYOR — sunucu mesaj yazmayı (gönder, geri al, okundu)
          bu kipte reddediyor; kutuyu gösterip hata ile karşılamak yerine sebebi önce söylüyoruz.
        */
        <div className="border-notr-200 border-t pt-3">
          <p role="status" className="text-notr-700 text-sm">
            Görünüm kipinde mesaj gönderilemez. Yazışmayı yalnız okuyabilirsiniz.
          </p>
        </div>
      ) : y.arsivlendiMi ? (
        /*
          ARŞİVLENMİŞ YAZIŞMADA YAZMA KUTUSU HİÇ ÇİZİLMİYOR — F-MSG-07.
          Kutuyu gösterip gönderince hata vermek, kullanıcıya yazdırıp
          sonra "olmadı" demek olur ve gerekçe ancak hatadan sonra
          görünür. Sebep burada, yazmadan ÖNCE duruyor.
        */
        <div className="border-notr-200 border-t pt-3">
          <p role="status" className="text-notr-700 text-sm">
            Bu yazışma arşivlendi; yeni mesaj gönderilemez. Geçmiş duruyor ve okunabilir.
          </p>
        </div>
      ) : (
        <div className="border-notr-200 flex flex-col gap-2 border-t pt-3">
          {y.mesai && !y.mesai.icindeMi && (
            /*
              MESAİ DIŞI NOTU — F-MSG-05. Mesaj yine gönderilebiliyor;
              engellenseydi gece yarısı bir şey anlatmak isteyen kişi
              onu hiç yazamaz ve sabaha unuturdu. Engellenen tek şey
              yanlış beklenti.

              Mesai İÇİNDEYKEN not GÖSTERİLMİYOR: "şu an mesaide" demek,
              cevabın hemen geleceği sözünü verirdi — oysa bu bir gelen
              kutusu, canlı destek değil.
            */
            <NotKutusu ton="bilgi">
              Şu an mesai saati dışında. Mesajınız iletilir; yanıt {y.mesai.metin} arasında gelir.
            </NotKutusu>
          )}
          <MetinAlani
            etiket="Mesajınız"
            etiketGizli
            rows={3}
            maxLength={MESAJ_EN_COK}
            placeholder="Mesajınızı yazın"
            value={taslak}
            onChange={(e) => setTaslak(e.target.value)}
          />
          {/*
            HAZIR YANIT YAPIŞTIRILIYOR, GÖNDERİLMİYOR — F-MSG-04.
            Doğrudan gönderseydik hazır yanıt, okunmadan gönderilen bir
            yanıt olurdu; karşıdaki kişi bir çocuk. Metin kutuya
            geliyor, koç okuyup düzenleyebiliyor.

            Var olan taslağın ÜSTÜNE YAZILMIYOR, sonuna ekleniyor:
            yazdığı iki cümleyi bir tıkla kaybetmek, geri alınamaz bir
            veri kaybı olurdu.
          */}
          {sablonlar.length > 0 && (
            <Secim
              etiket="Hazır yanıt ekle"
              etiketGizli
              value=""
              onChange={(e) => {
                const secilen = sablonlar.find((x) => x.id === e.target.value);
                if (secilen)
                  setTaslak((o) => (o.trim() ? `${o}\n\n${secilen.govde}` : secilen.govde));
              }}
              className="max-w-56"
            >
              <option value="">Hazır yanıt ekle…</option>
              {sablonlar.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.baslik}
                </option>
              ))}
            </Secim>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={dosyaAlani}
              type="file"
              aria-label="Dosya ekle"
              /*
                `accept` yalnızca bir İPUCU: kullanıcı yine de başka bir
                dosya seçebilir. Gerçek kontrol sunucuda ve İÇERİKTEN
                yapılıyor — `rapor.pdf` adıyla gönderilen bir görüntü
                dosyası orada yakalanıyor.
              */
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setDosya(e.target.files?.[0] ?? null)}
              className="text-notr-700 max-w-56 text-sm"
            />
            {dosya && (
              <Buton
                gorunum="sessiz"
                onClick={() => {
                  setDosya(null);
                  if (dosyaAlani.current) dosyaAlani.current.value = "";
                }}
              >
                Eki kaldır
              </Buton>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-notr-600 text-xs">
              Gönderdikten sonra {GERI_ALMA_DAKIKA} dakika içinde geri alabilirsiniz — karşı taraf
              okumadıysa. Ek: görsel veya PDF, en çok 8 MB.
            </p>
            <Buton
              onClick={() => gonder.mutate()}
              yukleniyor={gonder.isPending}
              yukleniyorMetni="Gönderiliyor"
              /*
                EKİ OLAN MESAJIN GÖVDESİ BOŞ OLABİLİR. Fotoğrafı yazı
                yazmadan göndermek sıradan bir kullanım; zorunlu
                kılsaydık kullanıcı tek karakter yazmayı öğrenir ve
                kural anlamını yitirirdi. Sunucu da aynı kuralı uyguluyor.
              */
              disabled={taslak.trim().length === 0 && dosya === null}
            >
              Gönder
            </Buton>
          </div>
          {hata && (
            <p role="alert" className="text-hata-700 text-sm">
              {hata}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function YeniYazisma({ onAcildi }: { onAcildi: (conversationId: string) => void }) {
  const qc = useQueryClient();
  const [alici, setAlici] = useState("");
  const [govde, setGovde] = useState("");
  const [hata, setHata] = useState<string | null>(null);

  const kisiler = useQuery({ queryKey: ["mesaj-kisiler"], queryFn: mesajKisileri, retry: false });

  /*
    KANAL SEÇENEKTEN DEĞİL SEÇİLEN KİŞİDEN ÇIKIYOR. Kullanıcıya "kanal"
    diye teknik bir kavram sormuyoruz: koçluk listesinden birini
    seçtiyse KOC, personel listesinden seçtiyse PERSONEL. Kanalı ayrıca
    sorsaydık kullanıcı yanlış kanalı seçip anlamsız bir ret alırdı.
  */
  const secenekler = useMemo(() => {
    const k = kisiler.data;
    if (!k) return [];
    return [
      ...k.koc.map((p) => ({ deger: `KOC:${p.userId}`, etiket: `${p.ad} — ${p.iliski}` })),
      /*
        ROL ADIN YANINDA — 13.09.2026, kullanıcı bildirdi.
        Önce herkesin yanında sabit "(personel)" yazıyordu; on altı
        kişilik bir kurumda "kime yazacağım" sorusunu addan çözmek
        gerekiyordu. Koç listesi zaten ilişkiyi yazıyor, personel
        listesi de rolü yazsın.

        Rol BOŞSA yalnızca ad: boş parantez ya da "—" ile biten bir
        satır, bilgi eksikliğini bilgiymiş gibi gösterir.
      */
      ...k.personel.map((p) => ({
        deger: `PERSONEL:${p.userId}`,
        /*
          `?.` BILINCLI — TIP ZORUNLU DESE DE.
          Panel ve API AYNI ANDA dagitilmiyor: yeni panel eski API ile
          birkac saniye birlikte yasiyor. O pencerede `roller` gelmez
          ve `p.roller.length` butun alici listesini cokertirdi —
          kullanici hic mesaj gonderemezdi. Testte de tam bu sekilde
          dustu (mock alani tasimiyordu).
        */
        etiket: p.roller?.length ? `${p.ad} — ${p.roller.join(", ")}` : p.ad,
      })),
    ];
  }, [kisiler.data]);

  const gonder = useMutation({
    mutationFn: () => {
      const ayrac = alici.indexOf(":");
      return mesajGonder({
        kanal: alici.slice(0, ayrac) as MesajKanali,
        aliciId: alici.slice(ayrac + 1),
        govde,
      });
    },
    onSuccess: async (s) => {
      setGovde("");
      setAlici("");
      setHata(null);
      await qc.invalidateQueries({ queryKey: ["mesaj-kutum"] });
      onAcildi(s.conversationId);
    },
    onError: (e) => setHata(e instanceof ApiHatasi ? e.message : "Mesaj gönderilemedi."),
  });

  if (kisiler.isPending) return <Iskelet satir={3} />;

  if (kisiler.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Kişi listesi alınamadı"
        aciklama={kisiler.error instanceof ApiHatasi ? kisiler.error.message : undefined}
      />
    );
  }

  if (secenekler.length === 0) {
    return (
      <p className="text-notr-700 text-sm">
        Şu an yazışabileceğiniz kimse yok. Koçluk kanalı yalnızca size atanmış öğrenciler ve
        velileriyle açılır; atama sona erdiğinde kişi listeden düşer.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Secim etiket="Kime" value={alici} onChange={(e) => setAlici(e.target.value)}>
        <option value="">Seçiniz</option>
        {secenekler.map((s) => (
          <option key={s.deger} value={s.deger}>
            {s.etiket}
          </option>
        ))}
      </Secim>
      <MetinAlani
        etiket="Mesaj"
        rows={4}
        maxLength={MESAJ_EN_COK}
        value={govde}
        onChange={(e) => setGovde(e.target.value)}
      />
      <div>
        <Buton
          onClick={() => gonder.mutate()}
          yukleniyor={gonder.isPending}
          yukleniyorMetni="Gönderiliyor"
          disabled={alici === "" || govde.trim().length === 0}
        >
          Gönder
        </Buton>
      </div>
      {hata && (
        <p role="alert" className="text-hata-700 text-sm">
          {hata}
        </p>
      )}
    </div>
  );
}

export function Mesajlar() {
  const [secili, setSecili] = useState<string | null>(null);
  const [yeni, setYeni] = useState(false);
  const ben = useQuery({ queryKey: ["ben"], queryFn: benGetir, retry: false });
  const gorunumde = ben.data?.gorunumde === true;

  const kutu = useQuery({
    queryKey: ["mesaj-kutum"],
    queryFn: mesajKutum,
    retry: false,
    /*
      Bu modülde ANLIK TESLİM YOK (`mesaj-kurallari.ts` notu): WebSocket
      gerektirirdi ve "çevrimiçi" göstergesi bir gözetim yüzeyidir.
      Aralıklı tazeleme, bekleyen bir cevabın makul sürede görünmesini
      sağlıyor; 60 sn bildirim ziliyle aynı ritim.
    */
    refetchInterval: 60_000,
  });

  if (kutu.isPending) return <Iskelet satir={4} />;

  if (kutu.isError) {
    return (
      <Kart baslik="Mesajlar">
        <BosDurum
          ton="hata"
          baslik="Gelen kutusu alınamadı"
          aciklama={kutu.error instanceof ApiHatasi ? kutu.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void kutu.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const { yazismalar, toplamOkunmamis } = kutu.data;

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Öğrenci, veli ve ekiple panel içinde yazışın." />
      <Kart
        baslik="Mesajlar"
        aciklama={
          toplamOkunmamis > 0
            ? `${toplamOkunmamis} okunmamış mesajınız var.`
            : "Okunmamış mesajınız yok."
        }
        eylem={
          gorunumde ? undefined : (
            <Buton gorunum={yeni ? "ikincil" : "birincil"} onClick={() => setYeni((o) => !o)}>
              {yeni ? "Vazgeç" : "Yeni mesaj"}
            </Buton>
          )
        }
      >
        {gorunumde && (
          <p role="status" className="text-notr-700 text-sm">
            Görünüm kipinde mesaj gönderilemez.
          </p>
        )}
        {!gorunumde && yeni && (
          <YeniYazisma
            onAcildi={(id) => {
              setYeni(false);
              setSecili(id);
            }}
          />
        )}
      </Kart>

      {/*
        MESAİ AYARI YALNIZCA KOÇTA ÇİZİLİYOR — bileşen `kocMu` false
        olduğunda hiçbir şey döndürmüyor. Boş bir "mesai saatlerim"
        kutusu, yöneticiye ait olmayan bir ayarı ona aitmiş gibi
        gösterirdi.
      */}
      <MesaiAyari />

      <MesajSablonlari />

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <Kart baslik="Gelen kutusu">
          {yazismalar.length === 0 ? (
            <p className="text-notr-700 text-sm">
              Henüz yazışmanız yok. &quot;Yeni mesaj&quot; ile başlatabilirsiniz.
            </p>
          ) : (
            <ul className="max-h-[32rem] overflow-y-auto">
              {yazismalar.map((y) => (
                <KutuSatiri
                  key={y.id}
                  yazisma={y}
                  secili={y.id === secili}
                  onSec={() => setSecili(y.id)}
                />
              ))}
            </ul>
          )}
        </Kart>

        <Kart className="bg-notr-50">
          {secili === null ? (
            /*
              UNİKOÇ mesaj ekranı boş durumu (tarama-1 §3.13): ortada gri balon ikonu + başlık + yönlendirme.
              Vurgu rengi UNİKOÇ'un indigosu DEĞİL, panelin mor-pembe dili (karar 07.10.2026).
            */
            <div className="text-notr-700 flex min-h-64 flex-col items-center justify-center gap-2 text-center">
              <Ikon ad="sohbet" boyut="buyuk" className="text-notr-300 size-12" />
              <p className="text-metin-ikincil text-2xl font-bold">Mesajlar</p>
              <p className="max-w-md text-sm">
                Soldan bir yazışma seçin. Yazışmayı açmak &quot;okundu&quot; sayılır ve karşı
                tarafın geri alma hakkı biter.
              </p>
            </div>
          ) : (
            <div className="flex h-[32rem] flex-col">
              <Yazisma key={secili} id={secili} gorunumde={gorunumde} />
            </div>
          )}
        </Kart>
      </div>
    </div>
  );
}
